import { TabsContent } from '@/components/ui/tabs';
import { ACCOUNTING_FRAMEWORK_LABELS } from '@/types/fund';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getCountryLabel } from '@/constants/countries';
import type { Fund } from '@/types/fund';
import { formatDate } from '@/utils/formatters';
import { FundMetricsCard } from './FundMetricsCard';
import { FundSummaryCards } from './FundSummaryCards';
import {
  useFundInvestorStatistics,
  useFundInvestors,
} from '@/hooks/react-query/useInvestors';
import { FundWaterfallCard } from './waterfall/FundWaterfallCard';

interface FundOverviewTabProps {
  fund: Fund;
}

export function FundOverviewTab({ fund }: FundOverviewTabProps) {
  const { data: fundStats } = useFundInvestorStatistics(fund.id);
  const { data: fundInvestors } = useFundInvestors(fund.id);

  return (
    <TabsContent value="overview" className="space-y-6 flex-1">
      <FundSummaryCards
        fund={fund}
        investorCount={fundStats?.total_investors ?? fundInvestors?.length ?? 0}
        totalCommitments={fundStats?.total_commitments ?? 0}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fund Information */}
        <Card>
          <CardHeader>
            <CardTitle>Fund Information</CardTitle>
            <CardDescription>
              Basic fund details and structure information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Legal Structure
                </p>
                <p className="text-sm text-foreground">
                  {fund.legal_structure?.toUpperCase()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Domicile
                </p>
                <p className="text-sm text-foreground">
                  {getCountryLabel(fund.domicile_country)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Base Currency
                </p>
                <p className="text-sm text-foreground">{fund.base_currency}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Accounting Framework
                </p>
                <p className="text-sm text-foreground">
                  {ACCOUNTING_FRAMEWORK_LABELS[fund.accounting_framework]}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Regulatory Regime
                </p>
                <p className="text-sm text-foreground">
                  {fund.regulatory_regime?.toUpperCase() || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  First Closing
                </p>
                <p className="text-sm text-foreground">
                  {fund.first_closing_date
                    ? formatDate(fund.first_closing_date)
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Final Closing
                </p>
                <p className="text-sm text-foreground">
                  {fund.final_closing_date
                    ? formatDate(fund.final_closing_date)
                    : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Accounts */}
        <Card>
          <CardHeader>
            <CardTitle>Bank Accounts</CardTitle>
            <CardDescription>
              Fund banking information and account details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {fund.bank_accounts && fund.bank_accounts.length > 0 ? (
              fund.bank_accounts.map((account, index) => (
                <div key={account.id}>
                  {index > 0 && <div className="border-t border-border pt-4" />}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground">
                        {account.account_name}
                      </p>
                      {account.is_primary && (
                        <Badge variant="secondary" className="text-xs">
                          Primary
                        </Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Bank
                      </p>
                      <p className="text-sm text-foreground">
                        {account.bank_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Currency
                      </p>
                      <p className="text-sm text-foreground">
                        {account.currency}
                      </p>
                    </div>
                    {account.iban && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          IBAN
                        </p>
                        <p className="text-sm text-foreground">
                          {account.iban}
                        </p>
                      </div>
                    )}
                    {account.swift_bic && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          SWIFT/BIC
                        </p>
                        <p className="text-sm text-foreground">
                          {account.swift_bic}
                        </p>
                      </div>
                    )}
                    {account.account_number && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Account Number
                        </p>
                        <p className="text-sm text-foreground">
                          {account.account_number}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                No bank accounts configured
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fund Metrics */}
      <FundMetricsCard fundId={fund.id} currency={fund.base_currency} />

      {/* Distribution Waterfall */}
      <FundWaterfallCard fundId={fund.id} currency={fund.base_currency} />
    </TabsContent>
  );
}
